/** Import base components, tabs, types, API functions, and utilities */
import type {BaseViewProps, BaseViewState} from "../base/ui_general/BaseView";
import {BaseView, BaseViewStateInitial} from "../base/ui_general/BaseView";
import {GeneralTab} from "./tabs/GeneralTab";
import {LiabilitiesTab} from "./tabs/LiabilitiesTab";
import {AssetsTab} from "./tabs/AssetsTab";
import {FormulasTab} from "./tabs/FormulasTab.tsx";
import {TriggersTab} from "./tabs/TriggersTab";
import {WaterfallsTab} from "./tabs/WaterfallsTab";
import autobind from "autobind-decorator";
import React, {createRef, type ReactElement} from "react";
import {calcHeightToBottomOffset, getQueryStringParameterAsUUID} from "../base/utils/GeneralUtilities";
import {ResultsTab} from "./tabs/ResultsTab.tsx";
import {VariablesTab} from "./tabs/VariablesTab.tsx";
import {RBSModal} from "../base/rbs_components/RBSModal.tsx";
import type {
    CashReserve,
    CashTranche,
    ChatHistory,
    Evaluation,
    Fee,
    Formula,
    Portfolio,
    Replenishment,
    SyntheticExcessSpread,
    SyntheticTranche,
    TimeSeries,
    Transaction,
    TransactionSettings,
    Trigger,
    Variable,
    Waterfall,
    WaterfallItem,
    WMT
} from "../base/ui_general/GeneralInterfaces.tsx";
import {getRequest_get_evaluation} from "../api/evaluation.tsx";
import {
    ApiEndPoints,
    DESIGN_CENTER_BOTTOM_OFFSET_PX,
    HTMLFormattedTextTypes,
    IDTypes,
    LabelTextMessageTypes
} from "../base/Settings";
import {DesignCenterTabEnum} from "./Interfaces";
import {getRequest_get_wmt} from "../api/wmt.tsx";
import Plot from 'react-plotly.js';
import {UserGuidance} from "../base/ui_components/UserGuidance.tsx";
import {VerticalSpacing} from "../base/ui_general/GeneralFunctionComponents.tsx";
import {Carousel, Col, Row} from 'react-bootstrap';
import {TimeSeriesTab} from "./tabs/TimeSeriesTab";
import {SettingsTab} from "./tabs/SettingsTab";
import {WMTTab} from "./tabs/WMTTab";
import {AIChatTab} from "./tabs/AIChatTab";
import styles from './tabs/TabShared.module.css';


/** Props for the DesignCenterOverView component */
interface DesignCenterOverViewProps extends BaseViewProps {
    /** The currently selected transaction, or undefined if none is selected. */
    transaction: Transaction | undefined;
    /** All variables for the transaction (ApiEndPoints.VARIABLE). */
    variables: Array<Variable> | undefined;
    /** All waterfall items (ApiEndPoints.WATERFALL_ITEM). */
    waterfall_items: Array<WaterfallItem> | undefined;
    /** All time series (ApiEndPoints.TIME_SERIES). */
    time_series: Array<TimeSeries> | undefined;
    /** All fees (ApiEndPoints.FEE). */
    fees: Array<Fee> | undefined;
    /** All waterfall structures (ApiEndPoints.WATERFALL). */
    waterfalls: Array<Waterfall> | undefined;
    /** All formulas (ApiEndPoints.FORMULA). */
    formulas: Array<Formula> | undefined;
    /** All triggers (ApiEndPoints.TRIGGER). */
    triggers: Array<Trigger> | undefined;
    /** All portfolios (ApiEndPoints.PORTFOLIO). */
    portfolios: Array<Portfolio> | undefined;
    /** All cash reserves (ApiEndPoints.CASH_RESERVE). */
    cash_reserves: Array<CashReserve> | undefined;
    /** All cash tranches (ApiEndPoints.CASH_TRANCHE). */
    cash_tranches: Array<CashTranche> | undefined;
    /** All synthetic tranches (ApiEndPoints.SYNTHETIC_TRANCHE). */
    synthetic_tranches: Array<SyntheticTranche> | undefined;
    /** All synthetic excess spreads (ApiEndPoints.SYNTHETIC_EXCESS_SPREAD). */
    synthetic_excess_spreads: Array<SyntheticExcessSpread> | undefined;
    /** All replenishments (ApiEndPoints.REPLENISHMENT). */
    replenishments: Array<Replenishment> | undefined;
    /** Transaction settings */
    transaction_settings: TransactionSettings | undefined;
    /** Chat transcript loaded for the current user/thread. */
    chat_history: ChatHistory | undefined;
}


/** State interface for DesignCenterOverView
 * Includes active tab, pool data, parameters, formulas, variables, triggers, logical operators, waterfalls,
 * evaluations, and the current transaction.
 * So far only WaterfallsTab, TriggersTab, GeneralTab, FormulasTab, ResultsTab and VariablesTab were handled.
 */
interface DesignCenterOverViewState extends BaseViewState {
    activeTab: DesignCenterTabEnum;
    /** Evaluation results for the current transaction */
    evaluation: Evaluation | undefined;
    /** WMT data for the current transaction */
    wmt: WMT | undefined;
    /** Loss distribution data for modal display */
    lossDistributionData: Array<number> | undefined;
    /** Title for the loss distribution modal */
    lossDistributionTitle: string | undefined;
    /** Tab currently hovered by mouse (for hover effect) */
    hoveredTab: DesignCenterTabEnum | null;
}


/**
 * Handles state initialization, API requests, localStorage persistence, and renders individual tabs.
 */
export class DesignCenterOverView extends BaseView<DesignCenterOverViewProps, DesignCenterOverViewState> {
    private evaluationInterval: ReturnType<typeof setInterval> | undefined;
    private leftPanelRef = createRef<HTMLDivElement>();
    private rightPanelRef = createRef<HTMLDivElement>();
    private contentPanelRef = createRef<HTMLDivElement>();

    /**
     * Constant array of all ApiEndPoints corresponding to entries in the props of this class.
     */
    static readonly FULL_DATA_LOAD_ENDPOINTS: Array<ApiEndPoints> = [
        ApiEndPoints.VARIABLE,
        ApiEndPoints.WATERFALL_ITEM,
        ApiEndPoints.TIME_SERIES,
        ApiEndPoints.FEE,
        ApiEndPoints.WATERFALL,
        ApiEndPoints.FORMULA,
        ApiEndPoints.PORTFOLIO,
        ApiEndPoints.TRIGGER,
        ApiEndPoints.CASH_RESERVE,
        ApiEndPoints.CASH_TRANCHE,
        ApiEndPoints.SYNTHETIC_TRANCHE,
        ApiEndPoints.SYNTHETIC_EXCESS_SPREAD,
        ApiEndPoints.REPLENISHMENT,
        ApiEndPoints.TRANSACTION_SETTINGS,
        ApiEndPoints.CHAT,
    ];


    /** Constructor initializes state, loads saved data from localStorage, or sets defaults for new transactions. */
    constructor(props: DesignCenterOverViewProps) {
        super(props);

        /** Initialize component state */
        this.state = {
            ...BaseViewStateInitial,
            activeTab: DesignCenterTabEnum.GENERAL,
            evaluation: undefined,
            wmt: undefined,
            lossDistributionData: undefined,
            lossDistributionTitle: undefined,
            hoveredTab: null,
        };

        this.props.provideModelIDs(getQueryStringParameterAsUUID(IDTypes.TRANSACTION));
    }


    /** Lifecycle: Runs after component mounts
     * Loads transaction data and evaluations if transaction ID exists, otherwise creates default empty values.
     */
    componentDidMount() {
        if (this.props.transaction?.id) {
            this.props.loadData(DesignCenterOverView.FULL_DATA_LOAD_ENDPOINTS);
        }

        this.evaluationInterval = setInterval(() => {
            this.fetchEvaluations();
        }, 2000);
    }


    /** Lifecycle: Cleanup function called when the component is unmounted. */
    componentWillUnmount() {
        // Clear polling interval when component unmounts
        if (this.evaluationInterval) {
            clearInterval(this.evaluationInterval);
        }
    }


    @autobind
    showLossDistribution(data: number[], title: string | undefined) {
        this.setState({
            lossDistributionData: data,
            lossDistributionTitle: title
        });
    }


    /** Lifecycle: Called when state or props update.
     * Persists triggers, logical operators, formulas, waterfalls, and variables to localStorage.
     */
    componentDidUpdate(_prevProps: DesignCenterOverViewProps) {
        if (this.props.transaction && (!_prevProps.transaction || _prevProps.transaction.id !== this.props.transaction.id)) {
            this.props.loadData(DesignCenterOverView.FULL_DATA_LOAD_ENDPOINTS);
        }
    }


    /** Fetches evaluation results from the API and updates state. */
    @autobind
    fetchEvaluations() {
        if (!this.props.transaction) {
            this.setState({evaluation: undefined});
            return;
        }
        this.processFetchRequest(
            getRequest_get_evaluation(this.props.transaction.id),
            (evaluation: Evaluation) => {
                this.setState({evaluation: evaluation});
            }
        );
    }


    /**
     * Fetches WMT data for the given scenario number and updates state.
     * If no transaction is present, sets wmt to undefined.
     */
    @autobind
    fetchWMT(scenarioNr: number) {
        if (!this.props.transaction) {
            this.setState({wmt: undefined});
            return;
        }
        this.processFetchRequest(
            getRequest_get_wmt(this.props.transaction.id, scenarioNr),
            (wmtData: WMT) => {
                this.setState({wmt: wmtData});
            }
        );
    }


    /** Updates the active tab in the state. */
    @autobind
    setActiveTab(tab: DesignCenterTabEnum) {
        this.setState({activeTab: tab});
    }

    /**
     * Renders the Loss Distribution Modal if lossDistributionData is present in state.
     * Returns a ReactElement containing the modal and histogram plot, or null if no data.
     */
    private renderLossDistributionModal(): ReactElement | null {
        if (!this.state.lossDistributionData) return null;
        return (
            <RBSModal
                show={!!this.state.lossDistributionData}
                size={"xl"}
                header_title={this.state.lossDistributionTitle}
                onClose={() => this.setState({lossDistributionData: undefined, lossDistributionTitle: undefined})}
                onClickPrimary={() => this.setState({
                    lossDistributionData: undefined,
                    lossDistributionTitle: undefined
                })}
                primary_button_label="Close"
                show_close_button={true}
            >
                <div style={{
                    width: '100%',
                    margin: '0 auto'
                }}>
                    <Plot
                        data={[{x: this.state.lossDistributionData, type: 'histogram'}]}
                        layout={{
                            autosize: true,
                            height: 400,
                            title: {text: this.state.lossDistributionTitle || "Loss Distribution"},
                        }}
                        useResizeHandler={true}
                        style={{width: '100%'}}
                    />
                </div>
            </RBSModal>
        );
    }

    /**
     * Renders the left panel containing evaluation results.
     * Returns a ReactElement with the ResultTabComponent.
     */
    private renderEvaluationPanel(): ReactElement {
        const heightToBottomOffset = calcHeightToBottomOffset(
            this.props.windowInnerHeight ?? 0,
            DESIGN_CENTER_BOTTOM_OFFSET_PX,
            this.leftPanelRef
        );
        return (
            <div
                ref={this.leftPanelRef}
                style={{
                    top: 200,
                    left: 0,
                    width: "100%",
                    height: heightToBottomOffset,
                    backgroundColor: "#EFEEEB",
                    borderRadius: "0.25rem",
                    padding: "1rem",
                    zIndex: 1000,
                    boxShadow: "2px 2px 8px rgba(0,0,0,0.1)",
                    border: "3px solid #ccc",
                    overflowY: "auto",
                }}
            >
                <ResultsTab
                    evaluation={this.state.evaluation}
                    waterfallItems={this.props.waterfall_items ?? []}
                    showLossDistribution={this.showLossDistribution}
                    transaction={this.props.transaction}
                    loadData={this.props.loadData}
                    displayMessage={this.props.displayMessage}
                    windowInnerHeight={this.props.windowInnerHeight}
                    user_language={this.props.user_language}
                />
            </div>
        );
    }

    renderVariablePanel(): ReactElement | null {
        const showVariableTab = [
            DesignCenterTabEnum.FORMULA,
            DesignCenterTabEnum.TRIGGERS,
            DesignCenterTabEnum.WATERFALL
        ].includes(this.state.activeTab);
        if (!showVariableTab) return null;
        const heightToBottomOffset = calcHeightToBottomOffset(
            this.props.windowInnerHeight ?? 0,
            DESIGN_CENTER_BOTTOM_OFFSET_PX,
            this.rightPanelRef
        );
        return (
            <div
                ref={this.rightPanelRef}
                style={{
                    top: 200,
                    right: 0,
                    width: "100%",
                    height: heightToBottomOffset,
                    backgroundColor: "#f0f0f0",
                    borderRadius: "0.25rem",
                    padding: "1rem",
                    zIndex: 1000,
                    boxShadow: "2px 2px 8px rgba(0,0,0,0.1)",
                    border: "3px solid #ccc",
                    overflowY: "auto",
                }}
            >
                <VariablesTab
                    variables={this.props.variables || []}
                    waterfall_items={this.props.waterfall_items || []}
                    transaction={this.props.transaction}
                    loadData={this.props.loadData}
                    displayMessage={this.props.displayMessage}
                    windowInnerHeight={this.props.windowInnerHeight}
                    user_language={this.props.user_language}
                />
            </div>
        );
    }

    /**
     * Renders all tab contents as Carousel.Items, with the active tab controlling the visible slide.
     */
    private renderTabCarousel(): ReactElement {
        const tabOrder = this.getTabOrder().map(tab => tab.name);
        const activeIndex = tabOrder.indexOf(this.state.activeTab);
        const heightToBottomOffset = calcHeightToBottomOffset(
            this.props.windowInnerHeight ?? 0,
            DESIGN_CENTER_BOTTOM_OFFSET_PX,
            this.contentPanelRef
        );
        return (
            <div
                ref={this.contentPanelRef}
                style={{
                    height: heightToBottomOffset,
                }}
            >
                <Carousel
                    activeIndex={activeIndex}
                    onSelect={i => this.setActiveTab(tabOrder[i])}
                    controls={false}
                    indicators={false}
                    interval={null}
                    className={styles.carouselFullHeight}
                >
                    {/* Render tab content in canonical order */}
                    {tabOrder.map(tabName => {
                        switch (tabName) {
                            case DesignCenterTabEnum.GENERAL:
                                return <Carousel.Item key={tabName} style={{height: '100%'}}>
                                    <GeneralTab
                                        transaction={this.props.transaction}
                                        loadData={this.props.loadData}
                                        displayMessage={this.props.displayMessage}
                                        windowInnerHeight={this.props.windowInnerHeight}
                                        user_language={this.props.user_language}
                                        portfolios={this.props.portfolios ?? []}
                                        cash_reserves={this.props.cash_reserves ?? []}
                                        cash_tranches={this.props.cash_tranches ?? []}
                                        synthetic_tranches={this.props.synthetic_tranches ?? []}
                                        waterfall_items={this.props.waterfall_items ?? []}
                                    />
                                </Carousel.Item>;
                            case DesignCenterTabEnum.LIABILITIES:
                                return <Carousel.Item key={tabName} style={{height: '100%'}}>
                                    <LiabilitiesTab
                                        transaction={this.props.transaction}
                                        loadData={this.props.loadData}
                                        displayMessage={this.props.displayMessage}
                                        windowInnerHeight={this.props.windowInnerHeight}
                                        user_language={this.props.user_language}
                                        cash_tranches={this.props.cash_tranches ?? []}
                                        synthetic_tranches={this.props.synthetic_tranches ?? []}
                                        cash_reserves={this.props.cash_reserves ?? []}
                                        synthetic_excess_spreads={this.props.synthetic_excess_spreads ?? []}
                                        fees={this.props.fees ?? []}
                                        waterfall_items={this.props.waterfall_items}
                                        time_series={this.props.time_series}
                                        variables={this.props.variables}
                                    />
                                </Carousel.Item>;
                            case DesignCenterTabEnum.ASSETS:
                                return <Carousel.Item key={tabName} style={{height: '100%'}}>
                                    <AssetsTab
                                        transaction={this.props.transaction}
                                        loadData={this.props.loadData}
                                        displayMessage={this.props.displayMessage}
                                        windowInnerHeight={this.props.windowInnerHeight}
                                        user_language={this.props.user_language}
                                        portfolios={this.props.portfolios ?? []}
                                        time_series={this.props.time_series ?? []}
                                        variables={this.props.variables ?? []}
                                    />
                                </Carousel.Item>;
                            case DesignCenterTabEnum.TIMESERIES:
                                return <Carousel.Item key={tabName} style={{height: '100%'}}>
                                    <TimeSeriesTab
                                        transaction={this.props.transaction}
                                        loadData={this.props.loadData}
                                        displayMessage={this.props.displayMessage}
                                        windowInnerHeight={this.props.windowInnerHeight}
                                        user_language={this.props.user_language}
                                        time_series={this.props.time_series ?? []}
                                    />
                                </Carousel.Item>;
                            case DesignCenterTabEnum.FORMULA:
                                return <Carousel.Item key={tabName} style={{height: '100%'}}>
                                    <FormulasTab
                                        transaction={this.props.transaction}
                                        loadData={this.props.loadData}
                                        displayMessage={this.props.displayMessage}
                                        windowInnerHeight={this.props.windowInnerHeight}
                                        user_language={this.props.user_language}
                                        formulas={this.props.formulas ?? []}
                                        variables={this.props.variables ?? []}
                                    />
                                </Carousel.Item>;
                            case DesignCenterTabEnum.TRIGGERS:
                                return <Carousel.Item key={tabName} style={{height: '100%'}}>
                                    <TriggersTab
                                        transaction={this.props.transaction}
                                        loadData={this.props.loadData}
                                        displayMessage={this.props.displayMessage}
                                        windowInnerHeight={this.props.windowInnerHeight}
                                        user_language={this.props.user_language}
                                        triggers={this.props.triggers ?? []}
                                        variables={this.props.variables ?? []}
                                    />
                                </Carousel.Item>;
                            case DesignCenterTabEnum.WATERFALL:
                                return <Carousel.Item key={tabName} style={{height: '100%'}}>
                                    <WaterfallsTab
                                        transaction={this.props.transaction}
                                        loadData={this.props.loadData}
                                        displayMessage={this.props.displayMessage}
                                        windowInnerHeight={this.props.windowInnerHeight}
                                        user_language={this.props.user_language}
                                        waterfalls={this.props.waterfalls ?? []}
                                        waterfall_items={this.props.waterfall_items ?? []}
                                        variables={this.props.variables ?? []}
                                    />
                                </Carousel.Item>;
                            case DesignCenterTabEnum.WMT:
                                return <Carousel.Item key={tabName} style={{height: '100%'}}>
                                    <WMTTab
                                        transaction={this.props.transaction}
                                        loadData={this.props.loadData}
                                        displayMessage={this.props.displayMessage}
                                        windowInnerHeight={this.props.windowInnerHeight}
                                        user_language={this.props.user_language}
                                        variables={this.props.variables ?? []}
                                        transaction_settings={this.props.transaction_settings}
                                        wmt={this.state.wmt}
                                        onScenarioSelected={this.fetchWMT}
                                    />
                                </Carousel.Item>;
                            case DesignCenterTabEnum.AI_CHAT:
                                return <Carousel.Item key={tabName} style={{height: '100%'}}>
                                    <AIChatTab
                                        transaction={this.props.transaction}
                                        loadData={this.props.loadData}
                                        displayMessage={this.props.displayMessage}
                                        windowInnerHeight={this.props.windowInnerHeight}
                                        user_language={this.props.user_language}
                                        chat_history={this.props.chat_history}
                                    />
                                </Carousel.Item>;
                            case DesignCenterTabEnum.SETTINGS:
                                return <Carousel.Item key={tabName} style={{height: '100%'}}>
                                    <SettingsTab
                                        transaction={this.props.transaction}
                                        loadData={this.props.loadData}
                                        displayMessage={this.props.displayMessage}
                                        windowInnerHeight={this.props.windowInnerHeight}
                                        user_language={this.props.user_language}
                                        transaction_settings={this.props.transaction_settings}
                                        time_series={this.props.time_series ?? []}
                                    />
                                </Carousel.Item>;
                            default:
                                return null;
                        }
                    })}
                </Carousel>
            </div>
        );
    }

    /**
     * Renders the tab navigation bar for the DesignCenter.
     * Returns a ReactElement containing the navigation tabs with bold labels, light/dark background colors, and hover effect.
     */
    private renderTabNavigation(): ReactElement {
        // Define very light background colors for each tab (inactive)
        const tabColors: { [key: string]: string } = {
            [DesignCenterTabEnum.GENERAL]: '#E0DFDA',
            [DesignCenterTabEnum.LIABILITIES]: '#EFEEEB',
            [DesignCenterTabEnum.ASSETS]: '#E0DFDA',
            [DesignCenterTabEnum.TIMESERIES]: '#EFEEEB',
            [DesignCenterTabEnum.FORMULA]: '#E0DFDA',
            [DesignCenterTabEnum.TRIGGERS]: '#EFEEEB',
            [DesignCenterTabEnum.WATERFALL]: '#E0DFDA',
            [DesignCenterTabEnum.WMT]: '#EFEEEB',
            [DesignCenterTabEnum.AI_CHAT]: '#E0DFDA',
            [DesignCenterTabEnum.SETTINGS]: '#EFEEEB',
        };
        // Define dark background colors for each tab (active)
        const tabColorsActive: { [key: string]: string } = {
            [DesignCenterTabEnum.GENERAL]: '#F07D00',
            [DesignCenterTabEnum.LIABILITIES]: '#F07D00',
            [DesignCenterTabEnum.ASSETS]: '#F07D00',
            [DesignCenterTabEnum.TIMESERIES]: '#F07D00',
            [DesignCenterTabEnum.FORMULA]: '#F07D00',
            [DesignCenterTabEnum.TRIGGERS]: '#F07D00',
            [DesignCenterTabEnum.WATERFALL]: '#F07D00',
            [DesignCenterTabEnum.WMT]: '#F07D00',
            [DesignCenterTabEnum.AI_CHAT]: '#F07D00',
            [DesignCenterTabEnum.SETTINGS]: '#F07D00',
        };
        // Define hover background colors for each tab (inactive)
        const tabColorsHover: { [key: string]: string } = {
            [DesignCenterTabEnum.GENERAL]: '#1E9BD7',
            [DesignCenterTabEnum.LIABILITIES]: '#1E9BD7',
            [DesignCenterTabEnum.ASSETS]: '#1E9BD7',
            [DesignCenterTabEnum.TIMESERIES]: '#1E9BD7',
            [DesignCenterTabEnum.FORMULA]: '#1E9BD7',
            [DesignCenterTabEnum.TRIGGERS]: '#1E9BD7',
            [DesignCenterTabEnum.WATERFALL]: '#1E9BD7',
            [DesignCenterTabEnum.WMT]: '#1E9BD7',
            [DesignCenterTabEnum.AI_CHAT]: '#1E9BD7',
            [DesignCenterTabEnum.SETTINGS]: '#1E9BD7',
        };
        // Use canonical TAB_ORDER for navigation
        return (
            <ul className="nav nav-tabs mb-2" style={{display: "flex", width: "100%", marginBottom: "0.5rem"}}>
                {this.getTabOrder().map((tab, idx) => {
                    const isActive = this.state.activeTab === tab.name;
                    const isHovered = this.state.hoveredTab === tab.name;
                    let backgroundColor = tabColors[tab.name];
                    let color = "#333";
                    if (isActive) {
                        backgroundColor = tabColorsActive[tab.name];
                        color = "#fff";
                    } else if (isHovered) {
                        backgroundColor = tabColorsHover[tab.name];
                    }
                    // Round upper left for first tab, upper right for last tab
                    let borderRadius = "0";
                    if (idx === 0) borderRadius = "0.75rem 0 0 0";
                    if (idx === this.getTabOrder().length - 1) borderRadius = "0 0.75rem 0 0";
                    return (
                        <li className="nav-item" key={tab.name} style={{flex: 1, display: "flex"}}>
                            <button
                                className={`nav-link${isActive ? " active" : ""}`}
                                onClick={() => this.setActiveTab(tab.name)}
                                onMouseEnter={() => this.setState({hoveredTab: tab.name})}
                                onMouseLeave={() => this.setState({hoveredTab: null})}
                                style={{
                                    backgroundColor,
                                    fontWeight: "bold",
                                    border: "1px solid #ddd",
                                    borderBottom: isActive ? `2px solid ${tabColorsActive[tab.name as DesignCenterTabEnum]}` : "1px solid #ddd",
                                    color,
                                    transition: "background-color 0.2s, color 0.2s",
                                    width: "100%",
                                    height: "100%",
                                    borderRadius,
                                }}
                            >
                                {tab.label}
                            </button>
                        </li>
                    );
                })}
            </ul>
        );
    }

    /** Returns the canonical tab order for the Design Center UI. */
    private getTabOrder(): Array<{ name: DesignCenterTabEnum; label: string }> {
        return [
            {name: DesignCenterTabEnum.GENERAL, label: "General"},
            {name: DesignCenterTabEnum.LIABILITIES, label: "Liabilities"},
            {name: DesignCenterTabEnum.ASSETS, label: "Assets"},
            {name: DesignCenterTabEnum.TIMESERIES, label: "Time Series"},
            {name: DesignCenterTabEnum.FORMULA, label: "Formula"},
            {name: DesignCenterTabEnum.TRIGGERS, label: "Triggers"},
            {name: DesignCenterTabEnum.WATERFALL, label: "Waterfall"},
            {name: DesignCenterTabEnum.WMT, label: "WMT"},
            {name: DesignCenterTabEnum.AI_CHAT, label: "AI Chat"},
            {name: DesignCenterTabEnum.SETTINGS, label: "Settings"},
        ];
    }

    /**
     * Returns the UserGuidance ReactElement for the current tab and language.
     */
    private renderUserGuidance(): ReactElement {
        const {activeTab} = this.state;
        const {user_language} = this.props;
        let caption: string;
        let explanation: string;
        let details_html: React.ReactNode;
        switch (activeTab) {
            case DesignCenterTabEnum.GENERAL:
                caption = this.getLabelTextMessage(LabelTextMessageTypes.GENERAL_TAB_CAPTION);
                explanation = this.getLabelTextMessage(LabelTextMessageTypes.GENERAL_TAB_EXPLANATION);
                details_html = this.getHTMLFormattedText(HTMLFormattedTextTypes.GENERAL_TAB_DETAILS);
                break;
            case DesignCenterTabEnum.LIABILITIES:
                caption = this.getLabelTextMessage(LabelTextMessageTypes.LIABILITIES_TAB_CAPTION);
                explanation = this.getLabelTextMessage(LabelTextMessageTypes.LIABILITIES_TAB_EXPLANATION);
                details_html = this.getHTMLFormattedText(HTMLFormattedTextTypes.LIABILITIES_TAB_DETAILS);
                break;
            case DesignCenterTabEnum.ASSETS:
                caption = this.getLabelTextMessage(LabelTextMessageTypes.ASSETS_TAB_CAPTION);
                explanation = this.getLabelTextMessage(LabelTextMessageTypes.ASSETS_TAB_EXPLANATION);
                details_html = this.getHTMLFormattedText(HTMLFormattedTextTypes.ASSETS_TAB_DETAILS);
                break;
            case DesignCenterTabEnum.TIMESERIES:
                caption = this.getLabelTextMessage(LabelTextMessageTypes.TIMESERIES_TAB_CAPTION);
                explanation = this.getLabelTextMessage(LabelTextMessageTypes.TIMESERIES_TAB_EXPLANATION);
                details_html = this.getHTMLFormattedText(HTMLFormattedTextTypes.TIMESERIES_TAB_DETAILS);
                break;
            case DesignCenterTabEnum.FORMULA:
                caption = this.getLabelTextMessage(LabelTextMessageTypes.FORMULA_TAB_CAPTION);
                explanation = this.getLabelTextMessage(LabelTextMessageTypes.FORMULA_TAB_EXPLANATION);
                details_html = this.getHTMLFormattedText(HTMLFormattedTextTypes.FORMULA_TAB_DETAILS);
                break;
            case DesignCenterTabEnum.TRIGGERS:
                caption = this.getLabelTextMessage(LabelTextMessageTypes.TRIGGERS_TAB_CAPTION);
                explanation = this.getLabelTextMessage(LabelTextMessageTypes.TRIGGERS_TAB_EXPLANATION);
                details_html = this.getHTMLFormattedText(HTMLFormattedTextTypes.TRIGGERS_TAB_DETAILS);
                break;
            case DesignCenterTabEnum.WATERFALL:
                caption = this.getLabelTextMessage(LabelTextMessageTypes.WATERFALL_TAB_CAPTION);
                explanation = this.getLabelTextMessage(LabelTextMessageTypes.WATERFALL_TAB_EXPLANATION);
                details_html = this.getHTMLFormattedText(HTMLFormattedTextTypes.WATERFALL_TAB_DETAILS);
                break;
            case DesignCenterTabEnum.WMT:
                caption = this.getLabelTextMessage(LabelTextMessageTypes.WMT_TAB_CAPTION);
                explanation = this.getLabelTextMessage(LabelTextMessageTypes.WMT_TAB_EXPLANATION);
                details_html = this.getHTMLFormattedText(HTMLFormattedTextTypes.WMT_TAB_DETAILS);
                break;
            case DesignCenterTabEnum.SETTINGS:
                caption = this.getLabelTextMessage(LabelTextMessageTypes.SETTINGS_TAB_CAPTION);
                explanation = this.getLabelTextMessage(LabelTextMessageTypes.SETTINGS_TAB_EXPLANATION);
                details_html = this.getHTMLFormattedText(HTMLFormattedTextTypes.SETTINGS_TAB_DETAILS);
                break;
            case DesignCenterTabEnum.AI_CHAT:
                caption = this.getLabelTextMessage(LabelTextMessageTypes.AI_CHAT_TAB_CAPTION);
                explanation = this.getLabelTextMessage(LabelTextMessageTypes.AI_CHAT_TAB_EXPLANATION);
                details_html = this.getHTMLFormattedText(HTMLFormattedTextTypes.AI_CHAT_TAB_DETAILS);
                break;
            default:
                caption = "";
                explanation = "";
                details_html = <></>;
        }
        return (
            <UserGuidance
                caption={caption}
                explanation={explanation}
                details_html={details_html}
                user_language={user_language}
            />
        );
    }

    /**
     * Main render function for DesignCenterOverView.
     * Renders navigation tabs, evaluation panel, variable panel, loss distribution modal, and the currently active tab content.
     * Returns the full layout as a ReactElement.
     */
    render() {
        return (
            <div>
                {this.renderLossDistributionModal()}

                {/* User Guidance */}
                {this.renderUserGuidance()}

                <VerticalSpacing/>

                <Row>
                    <Col sm={2}>
                        {this.renderEvaluationPanel()}
                    </Col>

                    <Col sm={8}>
                        {this.renderTabNavigation()}
                        {this.renderTabCarousel()}
                    </Col>

                    <Col sm={2}>
                        {this.renderVariablePanel()}
                    </Col>
                </Row>
            </div>
        );
    }
}
